/**
 * Haptic Feedback Utilities
 * Provides native haptic feedback on iOS/Android devices
 */

import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics'
import { Capacitor } from '@capacitor/core'

const isNative = Capacitor.isNativePlatform()

/**
 * Light haptic feedback - for UI interactions like button presses
 */
export async function hapticLight(): Promise<void> {
  if (!isNative) return
  try {
    await Haptics.impact({ style: ImpactStyle.Light })
  } catch (error) {
    console.warn('Haptic feedback not available:', error)
  }
}

/**
 * Medium haptic feedback - for confirmations and selections
 */
export async function hapticMedium(): Promise<void> {
  if (!isNative) return
  try {
    await Haptics.impact({ style: ImpactStyle.Medium })
  } catch (error) {
    console.warn('Haptic feedback not available:', error)
  }
}

/**
 * Heavy haptic feedback - for significant actions
 */
export async function hapticHeavy(): Promise<void> {
  if (!isNative) return
  try {
    await Haptics.impact({ style: ImpactStyle.Heavy })
  } catch (error) {
    console.warn('Haptic feedback not available:', error)
  }
}

/**
 * Success haptic feedback - for completed actions like habit completion
 */
export async function hapticSuccess(): Promise<void> {
  if (!isNative) return
  try {
    await Haptics.notification({ type: NotificationType.Success })
  } catch (error) {
    console.warn('Haptic feedback not available:', error)
  }
}

/**
 * Warning haptic feedback - for alerts or important notices
 */
export async function hapticWarning(): Promise<void> {
  if (!isNative) return
  try {
    await Haptics.notification({ type: NotificationType.Warning })
  } catch (error) {
    console.warn('Haptic feedback not available:', error)
  }
}

/**
 * Error haptic feedback - for failed actions
 */
export async function hapticError(): Promise<void> {
  if (!isNative) return
  try {
    await Haptics.notification({ type: NotificationType.Error })
  } catch (error) {
    console.warn('Haptic feedback not available:', error)
  }
}

/**
 * Selection changed haptic - for picker/selection changes
 */
export async function hapticSelection(): Promise<void> {
  if (!isNative) return
  try {
    await Haptics.selectionChanged()
  } catch (error) {
    console.warn('Haptic feedback not available:', error)
  }
}

/**
 * Vibrate for specified duration (Android only, iOS uses default)
 */
export async function hapticVibrate(duration: number = 300): Promise<void> {
  if (!isNative) return
  try {
    await Haptics.vibrate({ duration })
  } catch (error) {
    console.warn('Haptic feedback not available:', error)
  }
}

/**
 * Strong burst haptic - multiple impacts for explosive celebrations
 */
export async function hapticBurst(): Promise<void> {
  if (!isNative) return
  try {
    // Fire a burst of heavy haptics for maximum impact
    await Haptics.impact({ style: ImpactStyle.Heavy })
    setTimeout(() => Haptics.impact({ style: ImpactStyle.Heavy }), 80)
    setTimeout(() => Haptics.impact({ style: ImpactStyle.Heavy }), 160)
    setTimeout(() => Haptics.notification({ type: NotificationType.Success }), 250)
  } catch (error) {
    console.warn('Haptic feedback not available:', error)
  }
}

/**
 * Bell ring pattern - mini haptics timed to notification bell shake animation
 * Creates a "ding ding ding" feeling synced with the visual shake
 */
export async function hapticBellRing(): Promise<void> {
  if (!isNative) return
  try {
    await Haptics.impact({ style: ImpactStyle.Light })
    setTimeout(() => Haptics.impact({ style: ImpactStyle.Light }), 100)
    setTimeout(() => Haptics.impact({ style: ImpactStyle.Light }), 200)
    setTimeout(() => Haptics.selectionChanged(), 350)
  } catch (error) {
    console.warn('Haptic feedback not available:', error)
  }
}

/**
 * Drawer drag ratchet - creates a tactile "click" during drawer drag
 * Call repeatedly during drag to create a ratchet effect
 */
export async function hapticDragTick(): Promise<void> {
  if (!isNative) return
  try {
    await Haptics.selectionChanged()
  } catch (error) {
    console.warn('Haptic feedback not available:', error)
  }
}

/**
 * Celebration sequence - timed haptics for success screens
 * Creates a "THUMP-tick-tick-tick-TADA" pattern synced with animations
 */
export async function hapticCelebration(): Promise<void> {
  if (!isNative) return
  try {
    await Haptics.impact({ style: ImpactStyle.Heavy })
    setTimeout(() => Haptics.impact({ style: ImpactStyle.Light }), 150)
    setTimeout(() => Haptics.impact({ style: ImpactStyle.Light }), 250)
    setTimeout(() => Haptics.impact({ style: ImpactStyle.Light }), 350)
    setTimeout(() => Haptics.notification({ type: NotificationType.Success }), 500)
  } catch (error) {
    console.warn('Haptic feedback not available:', error)
  }
}

/**
 * Long press progress tick - escalating haptic feedback during a long press hold
 * Call this at throttled intervals (~60-120ms) with current progress (0 to 1)
 * Intensity ramps up as the user gets closer to completion
 */
export async function hapticLongPressTick(progress: number): Promise<void> {
  if (!isNative) return
  try {
    if (progress < 0.3) {
      // Subtle ratchet feel - gentle ticks
      await Haptics.selectionChanged()
    } else if (progress < 0.6) {
      // Building intensity - light impacts
      await Haptics.impact({ style: ImpactStyle.Light })
    } else if (progress < 0.85) {
      // Strong momentum - medium impacts
      await Haptics.impact({ style: ImpactStyle.Medium })
    } else {
      // Approaching climax - heavy impacts
      await Haptics.impact({ style: ImpactStyle.Heavy })
    }
  } catch (error) {
    console.warn('Haptic feedback not available:', error)
  }
}

/**
 * Long press milestone marker - fired at 25%, 50%, 75% for a satisfying "click"
 */
export async function hapticLongPressMilestone(): Promise<void> {
  if (!isNative) return
  try {
    await Haptics.notification({ type: NotificationType.Success })
  } catch (error) {
    console.warn('Haptic feedback not available:', error)
  }
}

/**
 * Completion burst - explosive haptic finale when long press reaches 100%
 * Creates a "BOOM-BOOM-BOOM-TADA-THUMP" pattern
 */
export async function hapticCompletionBurst(): Promise<void> {
  if (!isNative) return
  try {
    // Rapid-fire heavy impacts
    await Haptics.impact({ style: ImpactStyle.Heavy })
    setTimeout(() => Haptics.impact({ style: ImpactStyle.Heavy }), 50)
    setTimeout(() => Haptics.impact({ style: ImpactStyle.Heavy }), 100)
    // Success notification "TADA"
    setTimeout(() => Haptics.notification({ type: NotificationType.Success }), 180)
    // Final "THUMP"
    setTimeout(() => Haptics.impact({ style: ImpactStyle.Heavy }), 280)
  } catch (error) {
    console.warn('Haptic feedback not available:', error)
  }
}

// Convenience aliases for common use cases
export const haptic = {
  light: hapticLight,
  medium: hapticMedium,
  heavy: hapticHeavy,
  success: hapticSuccess,
  warning: hapticWarning,
  error: hapticError,
  selection: hapticSelection,
  vibrate: hapticVibrate,
  burst: hapticBurst,
  bellRing: hapticBellRing,
  dragTick: hapticDragTick,
  celebration: hapticCelebration,
  
  longPressTick: hapticLongPressTick,
  longPressMilestone: hapticLongPressMilestone,
  completionBurst: hapticCompletionBurst,
  
  // Specific use cases
  tap: hapticLight,
  press: hapticMedium,
  complete: hapticSuccess,
  achievement: hapticHeavy,
  streak: hapticBurst, // Strong burst for streak explosion!
  levelUp: hapticBurst,
  purchase: hapticSuccess,
}
